import React from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
} from '@material-ui/core';
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ptBR from 'dayjs/locale/pt-br';
import * as yup from 'yup';
import DayjsUtils from '@date-io/dayjs';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import { getYesOrNotOption } from 'services/activity';
import { useHistory } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { IGridFinishInspectionProps, IOption } from './types';
import useStyles from './styles';

dayjs.extend(customParseFormat);

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';

const FORM_ID = 'GRID_FINISH_INSPECTION_MODAL';

const MIN_DATE = dayjs().format('YYYY-MM-DD');
const MAX_DATE = dayjs('2100-01-01');

const GridFinishInpection = ({
  children,
  open,
  onClose,
  formData,
  formInfo,
  serviceData,
  extraServiceData,
  service,
  reloadData,
}: IGridFinishInspectionProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { account } = useAuth();

  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [predictedUseOptions, setPredictedUseOptions] = React.useState<
    IOption[]
  >([]);

  const shortName = React.useMemo(() => {
    if (!account?.username) return null;
    return account?.username.split('@')[0];
  }, [account]);

  const validationSchema = yup.object({
    startReal: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const test = dayjs(value);
        return (
          dayjs(value).isValid() &&
          (test.isSame(MIN_DATE) || test.isAfter(MIN_DATE))
        );
      }),
    completionReal: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      // eslint-disable-next-line func-names
      .test('is-greater-than', DEFAULT_ERROR_TEXT, function (value) {
        const test = dayjs(value);
        // eslint-disable-next-line react/no-this-in-sfc
        const { startReal } = this.parent;

        const validation =
          // não gera erro se o início previsto n for digitado
          !startReal ||
          // testa a data e verifica a validação de data mínima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          (test.isValid() &&
            (test.isSame(startReal, 'minute') ||
              test.isAfter(startReal, 'minute')));

        return validation;
      }),
    predictedUse: yup.string().required(DEFAULT_OK_TEXT),
  });

  const formik = useFormik({
    initialValues: {
      startReal: null,
      completionReal: null,
      predictedUse: '',
      inspectorTechnician: shortName,
    },
    validationSchema,
    onSubmit: formValues => {
      const values = {
        ...formValues,
        completionReal: dayjs(formValues.completionReal).format(
          'YYYY-MM-DDTHH:mmZ',
        ),
        startReal: dayjs(formValues.startReal).format('YYYY-MM-DDTHH:mmZ'),
      };

      const params = {
        checklistReplies: Object.values(serviceData),
        ...values,
        ...extraServiceData,
        inspectorTechnician: shortName,
      };

      setLoadingSaveForm(true);
      service(params)
        .then(({ data }) => {
          const id = data;
          if (id)
            history.push({
              pathname: `relatorio/${id}`,
              state: { canGoBack: true },
            });
          if (reloadData) reloadData();
          onClose(true);
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const enableQueries = React.useMemo(() => open, [open]);

  const enablePredictedUseOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryPredictedUseOptions = useQuery({
    queryKey: ['predictedUseOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setPredictedUseOptions(data);
    },
    enabled: enablePredictedUseOptions,
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const updateDate = React.useCallback(
    (fieldName, newDate, format) => {
      const date = dayjs(newDate, format, true);

      if (date.isValid()) {
        handleDateChange(fieldName, date.toISOString());
      } else {
        handleDateChange(fieldName, newDate);
      }

      if (!newDate) {
        handleDateChange(fieldName, null);
      }
    },
    [handleDateChange],
  );

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          {/* START ROW 0 */}
          <Grid>
            <Typography className={classes.formMessage}>
              Verifique as informações para finalizar inspeção:
            </Typography>
          </Grid>
          {/* END ROW 0 */}

          {/* START ROW 1 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                name="cluster"
                label="Cluster"
                variant="outlined"
                value={formData?.cluster}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="installation"
                label="Instalação"
                variant="outlined"
                value={formData?.installation}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {/* END ROW 1 */}

          {/* START ROW 2 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                name="equipment"
                label="Equipamento"
                variant="outlined"
                value={formData?.equipment}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="inspectorTechnician"
                label="Executor"
                variant="outlined"
                value={shortName}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {/* END ROW 2 */}

          {/* START ROW 3 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                name="outage"
                label="Indisp real"
                variant="outlined"
                value={formData?.outage}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="predictedUse"
                label="Aproveitamento real"
                select
                variant="outlined"
                value={formik.values.predictedUse}
                onChange={formik.handleChange}
                error={
                  (formik.touched.predictedUse &&
                    Boolean(formik.errors.predictedUse)) ||
                  !formik.values.predictedUse
                }
                helperText={
                  (formik.touched.predictedUse && formik.errors.predictedUse) ||
                  DEFAULT_HELPER_TEXT
                }
                inputProps={{
                  endAdornment: (
                    <LoadingInputEndAdornment
                      loading={queryPredictedUseOptions?.isLoading}
                    />
                  ),
                }}
              >
                {predictedUseOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          {/* END ROW 3 */}

          {/* START ROW 4 */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <KeyboardDateTimePicker
                id="startReal"
                name="startReal"
                label="Início real"
                inputVariant="outlined"
                format="DD/MM/YY - HH:mm"
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                clearable
                ampm={false}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                onChange={(date, value) => {
                  updateDate('startReal', value, 'DD/MM/YY - HH:mm');
                }}
                onBlur={formik.handleBlur}
                value={formik.values.startReal}
                error={
                  (formik.touched.startReal &&
                    Boolean(formik.errors.startReal)) ||
                  !formik.values.startReal
                }
                helperText={
                  (formik.touched.startReal && formik.errors.startReal) ||
                  DEFAULT_HELPER_TEXT
                }
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDateTimePicker
                id="completionReal"
                name="completionReal"
                label="Término real"
                inputVariant="outlined"
                format="DD/MM/YY - HH:mm"
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                clearable
                ampm={false}
                minDate={formik.values?.startReal || MIN_DATE}
                maxDate={MAX_DATE}
                onChange={(date, value) => {
                  updateDate('completionReal', value, 'DD/MM/YY - HH:mm');
                }}
                onBlur={formik.handleBlur}
                value={formik.values.completionReal}
                error={
                  (formik.touched.completionReal &&
                    Boolean(formik.errors.completionReal)) ||
                  !formik.values.completionReal
                }
                helperText={
                  (formik.touched.completionReal &&
                    formik.errors.completionReal) ||
                  DEFAULT_HELPER_TEXT
                }
              />
            </Grid>
          </Grid>
          {/* END ROW 4 */}

          {/* START ROW 5 */}
          <Grid container>
            {formInfo.map(({ title, value }, index) => (
              <Grid
                key={index}
                container
                justifyContent="space-between"
                alignItems="flex-start"
                className={classes.formInfoRow}
              >
                <Typography
                  className={classes.formInfoTitle}
                  variant="subtitle1"
                >
                  {title}
                </Typography>
                <Typography
                  className={classes.formInfoValue}
                  variant="subtitle2"
                >
                  {value}
                </Typography>
              </Grid>
            ))}
          </Grid>
          {/* END ROW 5 */}
        </Grid>
      </form>
    );
  }, [
    formik.handleSubmit,
    formik.values.predictedUse,
    formik.values.startReal,
    formik.values.completionReal,
    formik.handleChange,
    formik.touched.predictedUse,
    formik.touched.startReal,
    formik.touched.completionReal,
    formik.errors.predictedUse,
    formik.errors.startReal,
    formik.errors.completionReal,
    formik.handleBlur,
    classes.bodyContent,
    classes.formMessage,
    classes.formInfoRow,
    classes.formInfoTitle,
    classes.formInfoValue,
    formData?.cluster,
    formData?.installation,
    formData?.equipment,
    formData?.outage,
    shortName,
    queryPredictedUseOptions?.isLoading,
    predictedUseOptions,
    formInfo,
    updateDate,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={() => onClose(false)}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
      <ActionsButtonsBaseModal
        title="Finalizar inspeção"
        body={renderBody()}
        footer={renderFooter()}
        open={open}
        onClose={() => onClose(false)}
        width="628px"
      >
        {children}
      </ActionsButtonsBaseModal>
    </MuiPickersUtilsProvider>
  );
};

export default GridFinishInpection;
